import { useEffect, useState } from 'react'
import { Download, FileText } from 'lucide-react'
import {
  Modal, ModalContent, ModalHeader, ModalTitle, ModalBody, ModalFooter,
} from '@/components/ui/Modal'
import { apiClient } from '@/services/apiClient'
import { formatBytes } from '@/utils/formatBytes'
import type { ApprovedAsset } from '@/types/assets'

interface AssetPreviewModalProps {
  asset: ApprovedAsset | null
  onClose: () => void
}

/**
 * Mime-dispatched asset preview. The content endpoint is JWT-gated — a bare
 * <img src>/<iframe src> would 401 — so bytes are fetched via apiClient and
 * rendered from an object URL (MediaAttachment precedent). Download reuses
 * the same object URL. A video/* branch slots in here with PR 4.
 */
export function AssetPreviewModal({ asset, onClose }: AssetPreviewModalProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [shownAssetId, setShownAssetId] = useState<string | null>(null)

  // Render-time reset (React "adjust state during render" pattern): the
  // fetch effect runs after paint, which briefly left the previous asset's
  // bytes (or a revoked URL, on reopen) visible when the asset changed.
  if (asset && asset.id !== shownAssetId) {
    setShownAssetId(asset.id)
    setObjectUrl(null)
    setError(false)
  } else if (!asset && shownAssetId !== null) {
    setShownAssetId(null)
    setObjectUrl(null)
    setError(false)
  }

  // Keyed on the id, matching the render-time reset above — depending on the
  // asset OBJECT would refetch (and revoke the live URL) on a same-id
  // reference change without the reset firing.
  const assetId = asset?.id
  useEffect(() => {
    if (!assetId) return
    let cancelled = false
    let created: string | null = null
    apiClient.fetch(`/api/assets/${assetId}/content`)
      .then(async res => {
        if (!res.ok) throw new Error(`status ${res.status}`)
        const blob = await res.blob()
        if (cancelled) return
        created = URL.createObjectURL(blob)
        setObjectUrl(created)
      })
      .catch(() => { if (!cancelled) setError(true) })
    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [assetId])

  if (!asset) return null

  const isImage = asset.mimeType.startsWith('image/')
  const isPdf = asset.mimeType === 'application/pdf'
  const dot = asset.mediaRef.lastIndexOf('.')
  const ext = dot >= 0 ? asset.mediaRef.slice(dot) : ''

  const handleDownload = () => {
    if (!objectUrl) return
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = `${asset.slug}${ext}`
    // Attached before click: Safari has historically ignored clicks on
    // detached anchors.
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  return (
    <Modal open onOpenChange={open => { if (!open) onClose() }}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>{asset.name}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {error ? (
            <p className="text-sm text-destructive">Preview unavailable.</p>
          ) : !objectUrl ? (
            <p className="text-sm text-muted-foreground">Loading preview…</p>
          ) : isImage ? (
            <img
              src={objectUrl}
              alt={asset.name}
              className="max-h-[55vh] max-w-full mx-auto rounded-sm border border-border"
            />
          ) : isPdf ? (
            <object
              data={objectUrl}
              type="application/pdf"
              aria-label={`Preview of ${asset.name}`}
              className="w-full h-[55vh] rounded-sm border border-border"
            >
              <p className="text-sm text-muted-foreground p-4">
                PDF preview isn't supported in this browser — use Download below.
              </p>
            </object>
          ) : (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <FileText className="w-4 h-4" /> No inline preview for this file type.
            </p>
          )}
          <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Slug</dt>
            <dd className="text-foreground font-mono">{asset.slug}</dd>
            <dt className="text-muted-foreground">Type</dt>
            <dd className="text-foreground">{asset.mimeType}</dd>
            <dt className="text-muted-foreground">Size</dt>
            <dd className="text-foreground">{formatBytes(asset.sizeBytes)}</dd>
            {asset.tags.length > 0 && (
              <>
                <dt className="text-muted-foreground">Tags</dt>
                <dd className="flex flex-wrap gap-1">
                  {asset.tags.map(t => (
                    <span key={t} className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                      {t}
                    </span>
                  ))}
                </dd>
              </>
            )}
          </dl>
        </ModalBody>
        <ModalFooter>
          <button
            onClick={handleDownload}
            disabled={!objectUrl}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
